import { HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, ErrorStateService, LoggingService, MemberDetails, MemberService, MultipleAccountDetailItemModel } from '@fgb/core';

/** sessionStorage key holding the MLBAM auth code while the user is on the account selection page. */
export const MLBAM_AUTH_CODE_KEY = 'mlbamAuthCode';

/**
 * Landing component for the MLBAM auth-code SSO flow. The SSO redirect arrives here with a one-time
 * auth code in the `token` query parameter (e.g. /login/mlbam?token={code}).
 *
 * The component exchanges the code for the list of linked member accounts (without consuming it),
 * maps them into the shared `availableAccounts` store, stashes the code, then hands off to the
 * existing /login/accounts page to render the selection. Completing the selection there exchanges
 * the same code for auth tokens.
 */
@Component({
  selector: 'fgb-mlbam-account-selection',
  templateUrl: './mlbam-account-selection.component.html',
  styleUrls: ['./mlbam-account-selection.component.scss'],
})
export class MlbamAccountSelectionComponent implements OnInit {
  /** The one-time auth code read from the SSO redirect URL. */
  code: string;
  loading = true;

  constructor(
    private authService: AuthService,
    private memberService: MemberService,
    private errorService: ErrorStateService,
    private logService: LoggingService,
    private router: Router
  ) {}

  ngOnInit() {
    this.code = this.getQueryString('token', '');

    if (!this.code) {
      this.logService.warning('No auth code present in the MLBAM SSO redirect - returning to login.');
      this.router.navigate(['/login']);
      return;
    }

    this.fetchLinkedAccounts();
  }

  /** Exchange the auth code for the list of linked accounts (without consuming it) and hand off to the accounts page. */
  private fetchLinkedAccounts() {
    this.authService
      .getMlbamLinkedAccounts(this.code)
      .toPromise()
      .then((response) => {
        const members = (response && response.Members) || [];
        if (members.length === 0) {
          this.loading = false;
          this.logService.warning('No linked accounts returned for the MLBAM auth code.');
          this.errorService.addError('We could not find any accounts linked to your login.');
          return;
        }

        // Stash the code so the accounts page can complete the login on selection.
        sessionStorage.setItem(MLBAM_AUTH_CODE_KEY, this.code);
        this.memberService.setAvailableAccounts(members.map((m) => this.toMemberDetails(m)));
        this.router.navigate(['/login/accounts']);
      })
      .catch(() => {
        this.loading = false;
        this.logService.error('Failed to fetch MLBAM linked accounts - code may be invalid or expired.');
        this.errorService.clearErrors();
        this.errorService.addError('Your login session has expired. Please try logging in again.');
      });
  }

  /**
   * Map an MLBAM account item onto the MemberDetails shape the /login/accounts page renders.
   * EmailAddress2 is used by the old page as the per-account login URL, so LoginUrl is mapped there.
   */
  private toMemberDetails(member: MultipleAccountDetailItemModel): MemberDetails {
    return {
      FirstName: member.FirstName,
      Surname: member.Surname,
      EmailAddress1: member.EmailAddress,
      EmailAddress2: member.LoginUrl,
      ExternalRefNumber: member.ExternalRefNumber,
      MemberId: member.MemberId,
      UserAccountStatus: member.UserAccountStatus,
    } as MemberDetails;
  }

  /** Get a query string parameter without case sensitivity, mirroring the SSO component behaviour. */
  private getQueryString(parameter: string, defaultValue: string): string {
    const url = window.location.href;
    if (url.includes('?')) {
      const httpParams = new HttpParams({ fromString: url.split('?')[1] });
      const matchedKey = httpParams.keys().find((key) => key.toUpperCase() === parameter.toUpperCase());
      if (matchedKey) {
        return httpParams.get(matchedKey) || defaultValue;
      }
    }
    return defaultValue;
  }
}
